/**
 * QualityScaler — adapt rendering quality to sustained FPS.
 *
 * Watches an FpsMonitor; if the FPS sits below `lowFps` for `dwellMs`, it
 * downgrades the quality tier. If it sits above `highFps` for `dwellMs`,
 * it upgrades.
 *
 * Tiers (low → high):
 *   0  half DPR, no particles, no post-fx
 *   1  half DPR, particles ok, no post-fx
 *   2  full DPR, particles ok, no post-fx
 *   3  full DPR, particles ok, post-fx ok
 *
 * Game scenes can read `qualityScaler.tier` and use the convenience getters
 * (`particlesAllowed`, `postFxAllowed`) to gate optional effects.
 */
export class QualityScaler {
  /**
   * @param {object} opts
   * @param {import('./FpsMonitor.js').FpsMonitor} opts.fpsMonitor
   * @param {Phaser.Game} opts.game
   * @param {number} [opts.lowFps=28]
   * @param {number} [opts.highFps=55]
   * @param {number} [opts.dwellMs=2000]
   */
  constructor(opts) {
    this.fps = opts.fpsMonitor;
    this.game = opts.game;
    this.lowFps = opts.lowFps || 28;
    this.highFps = opts.highFps || 55;
    this.dwellMs = opts.dwellMs || 2000;

    this.tier = 3;
    this._sinceMs = 0;
    this._direction = 0;

    this.onTierChanged = null;
  }

  get particlesAllowed() { return this.tier >= 1; }
  get postFxAllowed() { return this.tier >= 3; }

  update(deltaMs) {
    const fps = this.fps.getFps();
    let dir = 0;
    if (fps < this.lowFps && this.tier > 0) dir = -1;
    else if (fps > this.highFps && this.tier < 3) dir = 1;

    if (dir === this._direction && dir !== 0) {
      this._sinceMs += deltaMs;
      if (this._sinceMs >= this.dwellMs) {
        this._applyTierChange(this.tier + dir);
        this._sinceMs = 0;
        this._direction = 0;
      }
    } else {
      this._direction = dir;
      this._sinceMs = 0;
    }
  }

  _applyTierChange(newTier) {
    this.tier = Math.max(0, Math.min(3, newTier));
    // Half DPR for tiers 0–1.
    const wantedDpr = this.tier <= 1 ? 0.5 : 1;
    if (this.game && this.game.scale) {
      try { this.game.scale.setZoom(wantedDpr); } catch (_) { /* ignore */ }
    }
    if (this.onTierChanged) this.onTierChanged(this.tier);
  }
}
